import { useSearchParams } from 'react-router-dom';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { useUserStore } from '../store/userStore';
import { isPersonaGated } from '../lib/personas';
import { PMDashboard } from './PMDashboard';
import { ExecutiveDashboard } from './ExecutiveDashboard';
import { TeamLeadDashboard } from './TeamLeadDashboard';
import { EngineerDashboard } from './EngineerDashboard';
import { DesignerDashboard } from './DesignerDashboard';
import { SalesDashboard } from './SalesDashboard';
import { MarketingDashboard } from './MarketingDashboard';
import { TodayView } from './TodayView';
import type { FunctionalRole } from '../types';

function dashboardForRole(
  role: FunctionalRole | string | undefined,
  onBackToToday: () => void,
  legacyPersonasEnabled: boolean,
) {
  const props = { onBackToToday };

  // Gated personas fall through to PMDashboard, same as any other
  // unrecognized role — see the 'default' case below. This is dispatch-time
  // enforcement: it doesn't matter whether the role came from an explicit
  // nav click, a stale activeRole, or primaryRole — a gated role never
  // renders its own dashboard while the flag is off.
  const resolvedRole = isPersonaGated(role, legacyPersonasEnabled) ? undefined : role;

  switch (resolvedRole) {
    case 'executive':    return <ExecutiveDashboard {...props} />;
    case 'team_lead':    return <TeamLeadDashboard {...props} />;
    case 'engineer':     return <EngineerDashboard {...props} />;
    case 'designer':     return <DesignerDashboard {...props} />;
    case 'sales':        return <SalesDashboard {...props} />;
    case 'marketing':    return <MarketingDashboard {...props} />;
    // useEffectiveRole()'s terminal fallback is 'product_manager', a real
    // FunctionalRole, so 'default' below already covers it — no 'viewer'
    // case needed (it used to incorrectly map 'viewer' to
    // <ExecutiveDashboard>, the opposite of "most conservative").
    default:             return <PMDashboard {...props} />;
  }
}

// ── Dispatch ───────────────────────────────────────────────────────────────

export function RoleDashboard() {
  // viewMode lives in the URL (not component state) so links elsewhere —
  // notably ProjectDashboard's breadcrumb — can target the portfolio view
  // directly instead of always landing back on "/" (which used to reset to
  // 'today' no matter where the user came from).
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: 'today' | 'portfolio' =
    searchParams.get('view') === 'portfolio' ? 'portfolio' : 'today';

  const effectiveRole = useEffectiveRole();
  const legacyPersonasEnabled = useUserStore((s) => s.sessionConfig?.legacyPersonasEnabled ?? false);

  if (viewMode === 'today') {
    return <TodayView onBrowseAll={() => setSearchParams({ view: 'portfolio' })} />;
  }

  return dashboardForRole(effectiveRole, () => setSearchParams({}), legacyPersonasEnabled);
}
